import React from 'react';
import { Link } from 'react-router-dom';
import * as PhosphorIcons from '@phosphor-icons/react';
import { TOOLS, CATEGORIES } from '../lib/tools';
import { Sparkles, ArrowRight } from 'lucide-react';

function ToolIcon({ name, color, size = 24 }) {
  const Icon = PhosphorIcons[name] || PhosphorIcons.File;
  return <Icon size={size} weight="duotone" color={color} />;
}

export default function ToolGrid({ filter = null }) {
  const [active, setActive] = React.useState(filter || 'All');
  const cats = ['All', ...CATEGORIES];
  const filtered = active === 'All' ? TOOLS : TOOLS.filter((t) => t.category === active);

  return (
    <section id="tools" data-testid="tool-grid-section" className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-vault-gold mb-3">The Toolkit</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tighter text-white">
            37 Tools. <span className="gold-text">Zero Limits.</span>
          </h2>
          <p className="mt-4 text-gray-400 max-w-xl">
            Every document operation you'll ever need — engineered for speed and locked in our vault.
          </p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`category-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => setActive(c)}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition ${
              active === c
                ? 'bg-vault-gold text-vault-paper shadow-gold-glow'
                : 'bg-vault-paper/60 text-gray-300 border border-white/10 hover:border-vault-gold/50'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {filtered.map((tool) => (
          <Link
            key={tool.slug}
            to={`/${tool.slug}`}
            data-testid={`tool-card-${tool.slug}`}
            className={`tool-card rounded-xl p-5 group relative overflow-hidden ${!tool.available ? 'opacity-60' : ''}`}
          >
            {tool.ai && (
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-vault-gold/10 border border-vault-gold/30 px-2 py-0.5 rounded-full">
                <Sparkles className="w-3 h-3 text-vault-gold" />
                <span className="text-[10px] font-mono text-vault-gold tracking-wider">AI</span>
              </div>
            )}
            {!tool.available && (
              <div className="absolute top-3 right-3 text-[10px] tracking-wider text-gray-500 font-mono uppercase">
                Soon
              </div>
            )}
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
              style={{ background: `${tool.color}15`, border: `1px solid ${tool.color}30` }}
            >
              <ToolIcon name={tool.icon} color={tool.color} size={26} />
            </div>
            <h3 className="font-heading font-semibold text-white text-base mb-1 group-hover:text-vault-gold transition">
              {tool.name}
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed">{tool.description}</p>
            {tool.available && (
              <div className="mt-3 flex items-center text-[11px] text-vault-gold/70 font-mono tracking-wider opacity-0 group-hover:opacity-100 transition">
                OPEN TOOL <ArrowRight className="w-3 h-3 ml-1" />
              </div>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}
